import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Coins, Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, Users, UserPlus } from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Crédits de tous les clients, par module (lecture seule)
// Disposition : en-tête FIXE (titre + statistiques + filtres), liste qui DÉFILE
// (en-tête de colonnes figé), pagination en bas.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const fmt = (n) => (n || 0).toLocaleString('fr-FR');
const PAGE_SIZES = [10, 25, 50, 100];

// Chaque module : libellé, valeur numérique (tri / filtre / total), affichage, détail au survol
const MODULES = [
  { key: 'ia_video', label: 'IA Studio Vidéo', unit: 'Ar',
    value: c => c.ia_video.balance, show: c => `${fmt(c.ia_video.balance)} Ar`,
    hint: c => `Acheté ${fmt(c.ia_video.bought)} Ar · Utilisé ${fmt(c.ia_video.used)} Ar` },
  { key: 'ia_image', label: 'IA Studio Image', unit: 'Ar',
    value: c => c.ia_image.balance, show: c => `${fmt(c.ia_image.balance)} Ar`,
    hint: c => `Acheté ${fmt(c.ia_image.bought)} Ar · Utilisé ${fmt(c.ia_image.used)} Ar` },
  { key: 'video_ads', label: 'Pub Vidéos', unit: 'vidéos',
    value: c => c.video_ads.remaining, show: c => `${fmt(c.video_ads.remaining)} vid.`,
    hint: c => `${fmt(c.video_ads.used)} utilisées sur ${fmt(c.video_ads.max)}` },
  { key: 'marketing', label: 'Marketing Studio', unit: '',
    value: c => c.marketing.images_remaining + c.marketing.videos_remaining,
    show: c => `${fmt(c.marketing.images_remaining)} img · ${fmt(c.marketing.videos_remaining)} vid.`,
    hint: () => 'Images et vidéos restantes' },
  { key: 'vtryon', label: 'Essayage virtuel', unit: '',
    value: c => c.vtryon.images + c.vtryon.videos,
    show: c => `${fmt(c.vtryon.images)} img · ${fmt(c.vtryon.videos)} vid.`,
    hint: () => 'Pool utilisateur (avant attribution aux pages)' },
  { key: 'cm', label: 'CM Studio', unit: 'Ar',
    value: c => c.cm.balance, show: c => `${fmt(c.cm.balance)} Ar`,
    hint: c => c.cm.pages.map(p => `${p.page} : ${fmt(p.balance)} Ar`).join('\n') || 'Aucune page avec solde' },
  { key: 'cv', label: 'Recrutement (CV)', unit: '',
    value: c => c.cv.balance, show: c => fmt(c.cv.balance),
    hint: () => 'Crédits CV' },
];

const SEVEN_DAYS = 7 * 24 * 3600 * 1000;

const fmtDate = (iso) => {
  if (!iso) return { date: '—', ago: '' };
  const d = new Date(iso);
  if (isNaN(d)) return { date: '—', ago: '' };
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const ago = days <= 0 ? "aujourd'hui" : days === 1 ? 'hier' : days < 30 ? `il y a ${days} j` : days < 365 ? `il y a ${Math.floor(days / 30)} mois` : `il y a ${Math.floor(days / 365)} an${days >= 730 ? 's' : ''}`;
  return { date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }), ago };
};

export default function ClientsCredits() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [onlyWithCredits, setOnlyWithCredits] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/clients-credits`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setUsers(d.users); else setError(d.message || 'Chargement impossible.');
    } catch { setError('Erreur réseau.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Retour à la page 1 dès qu'un filtre / tri / taille de page change
  useEffect(() => { setPage(1); }, [query, onlyWithCredits, moduleFilter, sortKey, pageSize]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = users.filter(u => !q || `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(q));
    if (moduleFilter !== 'all') {
      const m = MODULES.find(x => x.key === moduleFilter);
      list = list.filter(u => m.value(u.credits) > 0);
    } else if (onlyWithCredits) {
      list = list.filter(u => u.has_any);
    }
    if (sortKey === 'created_asc') {
      list = [...list].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
    } else if (sortKey !== 'created') {
      const m = MODULES.find(x => x.key === sortKey);
      list = [...list].sort((a, b) => m.value(b.credits) - m.value(a.credits));
    }
    return list;
  }, [users, query, onlyWithCredits, moduleFilter, sortKey]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Statistiques globales (tous les clients, indépendantes des filtres)
  const stats = useMemo(() => {
    const totals = {};
    MODULES.forEach(m => { totals[m.key] = users.reduce((sum, u) => sum + m.value(u.credits), 0); });
    const now = Date.now();
    return {
      totals,
      clients: users.length,
      withCredits: users.filter(u => u.has_any).length,
      recent: users.filter(u => u.created_at && now - new Date(u.created_at).getTime() < SEVEN_DAYS).length,
    };
  }, [users]);

  const statCards = [
    { label: 'Clients', value: fmt(stats.clients), sub: `${fmt(stats.withCredits)} avec crédits`, icon: Users },
    { label: 'Nouveaux (7 j)', value: fmt(stats.recent), sub: 'inscriptions', icon: UserPlus },
    ...MODULES.map(m => ({
      label: m.label,
      value: m.key === 'marketing' || m.key === 'vtryon'
        ? fmt(stats.totals[m.key]) : `${fmt(stats.totals[m.key])}${m.unit ? ' ' + m.unit : ''}`,
      sub: `${fmt(users.filter(u => m.value(u.credits) > 0).length)} clients`,
    })),
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-screen p-3 md:p-5 gap-3 box-border">
      {/* ── PARTIE FIXE : titre, statistiques, filtres ── */}
      <div className="flex-shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2.5">
              <Coins className="w-7 h-7 text-yellow-400" /> Crédits des clients
            </h1>
            <p className="text-xs text-blue-200">Tous les clients et leurs crédits restants, module par module. Lecture seule.</p>
          </div>
          <button onClick={load} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white" title="Actualiser">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2">
          {statCards.map(c => (
            <div key={c.label} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <div className="text-gray-400 text-[10px] flex items-center gap-1 truncate">
                {c.icon && <c.icon className="w-3 h-3 flex-shrink-0" />} {c.label}
              </div>
              <div className="text-white font-bold text-base leading-tight truncate">{c.value}</div>
              <div className="text-gray-500 text-[10px] truncate">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher un client (nom, email, téléphone)…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400" />
          </div>
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-white/20 text-white text-sm">
            <option value="all">Tous les modules</option>
            {MODULES.map(m => <option key={m.key} value={m.key}>Avec crédit : {m.label}</option>)}
          </select>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-white/20 text-white text-sm">
            <option value="created">Tri : plus récents</option>
            <option value="created_asc">Tri : plus anciens</option>
            {MODULES.map(m => <option key={m.key} value={m.key}>Tri : {m.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-gray-200 text-sm px-2 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={onlyWithCredits} onChange={e => setOnlyWithCredits(e.target.checked)} />
            Seulement avec crédits
          </label>
        </div>
        {error && <p className="text-red-300 text-sm">{error}</p>}
      </div>

      {/* ── LISTE QUI DÉFILE ── */}
      {loading && users.length === 0 ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto bg-white/5 border border-white/10 rounded-2xl">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="text-left text-gray-400 text-xs border-b border-white/10">
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">Inscrit le</th>
                {MODULES.map(m => <th key={m.key} className="px-3 py-3 font-medium whitespace-nowrap">{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(u => (
                <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2.5">
                    <div className="text-white font-medium">{u.name || '(sans nom)'}</div>
                    <div className="text-gray-400 text-xs">{u.email && !u.email.endsWith('@phone.teckia.local') ? u.email : u.phone}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="text-gray-200 text-xs">{fmtDate(u.created_at).date}</div>
                    <div className="text-gray-500 text-[10px]">{fmtDate(u.created_at).ago}</div>
                  </td>
                  {MODULES.map(m => {
                    const has = m.value(u.credits) > 0;
                    return (
                      <td key={m.key} className="px-3 py-2.5 whitespace-nowrap" title={m.hint(u.credits)}>
                        <span className={has ? 'text-green-300 font-medium' : 'text-gray-600'}>{has ? m.show(u.credits) : '—'}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={MODULES.length + 2} className="px-4 py-8 text-center text-gray-500">Aucun client trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── PAGINATION FIXE EN BAS ── */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Par page</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
            className="px-2 py-1 rounded-lg bg-slate-800 border border-white/20 text-white text-xs">
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-xs text-gray-400">
            {rows.length === 0 ? '0 client' : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, rows.length)} sur ${rows.length} client${rows.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs">Page {safePage} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={safePage >= pageCount}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
